import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useIsBasketPage} from '@aboutyou/router';
import {Z_INDEXES} from '@aboutyou/ui-components';
import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {useDeliveriesSection} from '../../../../backend-services/Basket/hooks/useGetBasketPageSection';
import {AddToBasketSuccessFlyoutWithRecos} from '../../../AddToBasketSuccessFlyout/AddToBasketSuccessFlyoutWithRecos';
import {useSuccessFlyoutOpenedState} from '../../../AddToBasketSuccessFlyout/hooks/useSuccessFlyoutOpenedState';
import {MiniAdpDialogOpenedStateKey} from '../../../MiniADPDialog/useMiniAdpDialogOpenedState';
import {ProductMiniAdpWithBasketFlyout} from '../../../ProductMiniAdpWithBasketFlyout/ProductMiniAdpWithBasketFlyout';
import {AddToBasketFlyout} from '../../../SizeSelectionFlyout/AddToBasketFlyout';
import {AddToBasketOnSuccessProps} from '../../../SizeSelectionFlyout/hooks/useAddToBasket';
import {SizeSelectionOpenedStateKey} from '../../../SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {useSizeSelectionOpenedStateManager} from '../hooks/useSizeSelectionOpenedStateManager';

type MiniAdpProps = {
  renderMiniADP: boolean;
};

type SizeSelectionProps = {
  selectedProduct?: Product;
};

// if we pass render mini adp as true, we render the mini adp, otherwise we render the size selection flyout
type CouponAddToBasketProps = MiniAdpProps | SizeSelectionProps;

// type guard
const isMiniAdpProps = (props: CouponAddToBasketProps): props is MiniAdpProps =>
  'renderMiniADP' in props && props.renderMiniADP;

export const CouponAddToBasket: FC<CouponAddToBasketProps> = props => {
  const {close, isOpen} = useSizeSelectionOpenedStateManager({
    isMiniADP: Boolean('renderMiniADP' in props && props.renderMiniADP),
  });
  const isBasketPage = useIsBasketPage();
  const deliveriesSection = useDeliveriesSection({
    disabled: !isBasketPage,
  });

  const handleOnSuccess = () => {
    if (isBasketPage) {
      deliveriesSection.refetch();
    }
  };

  if (isMiniAdpProps(props) && props.renderMiniADP) {
    return (
      <ProductMiniAdpWithBasketFlyout
        miniADPModalStateManagerKey={
          MiniAdpDialogOpenedStateKey.COUPON_WALLET_MAIN_MINI_ADP
        }
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.COUPON_WALLET_SIZE_SELECTION
        }
        showAddToWishlistButton
        renderInPortal
        onSuccess={handleOnSuccess}
        basketFlyoutStateManagerKey="couponWallet"
      />
    );
  }

  if (!isMiniAdpProps(props) && props.selectedProduct) {
    return (
      <SizeSelectionFlyout
        selectedProduct={props.selectedProduct}
        isOpen={isOpen}
        close={close}
        onSuccess={handleOnSuccess}
      />
    );
  }

  return null;
};

const SizeSelectionFlyout: React.FC<{
  isOpen: boolean;
  selectedProduct?: Product;
  close: VoidFunction;
  onSuccess: VoidFunction;
}> = ({isOpen, selectedProduct, close, onSuccess}) => {
  const [freeDeliverySection, setFreeDeliverySection] =
    useState<FreeDeliverySection>();
  const [statusSuccessSections, setStatusSuccessSections] = useState<
    AddItemResponseStatus_Success_Section[]
  >([]);
  const {
    isOpen: isSuccessFlyoutOpen,
    open: openSuccessFlyout,
    cancel: closeSuccessFlyout,
  } = useSuccessFlyoutOpenedState('coupon_wallet');

  const onCloseAddToBasketSuccessFlyout = () => {
    closeSuccessFlyout();
  };

  const onCloseAddToBasketFlyout = () => {
    close();
  };

  const onAddToBasketFlyoutSuccess = ({
    freeDeliverySection,
    statusSuccessSections,
  }: AddToBasketOnSuccessProps) => {
    setFreeDeliverySection(freeDeliverySection);
    setStatusSuccessSections(statusSuccessSections);
    openSuccessFlyout();

    onSuccess();
  };

  if (!selectedProduct) {
    return null;
  }

  return (
    <>
      <StyledAddToBasketFlyout
        isOpen={isOpen}
        onClose={onCloseAddToBasketFlyout}
        product={selectedProduct}
        onSuccess={onAddToBasketFlyoutSuccess}
        renderInPortal
        disableCouponsSlider
        badgesListKeys={[]}
      />
      <StyledAddToBasketSuccessFlyoutWithRecos
        productId={selectedProduct.id}
        show={isSuccessFlyoutOpen}
        sizeSelectionKey={
          SizeSelectionOpenedStateKey.COUPON_WALLET_SIZE_SELECTION
        }
        onClose={onCloseAddToBasketSuccessFlyout}
        freeDeliverySection={freeDeliverySection}
        statusSuccessSections={statusSuccessSections}
        renderInPortal
        selectedSize={undefined}
      />
    </>
  );
};

const StyledAddToBasketFlyout = styled(AddToBasketFlyout)`
  &&& {
    z-index: ${Z_INDEXES.SIZE_SELECTION_FLYOUT_ON_WALLET};
  }
`;

const StyledAddToBasketSuccessFlyoutWithRecos = styled(
  AddToBasketSuccessFlyoutWithRecos,
)`
  &&& {
    z-index: ${Z_INDEXES.SUCCESS_ADD_TO_BASKET_FLYOUT_ON_WALLET};
  }
`;
